/**
 * ValuationSankeyChart - Revenue-to-Cost flow Sankey chart.
 *
 * Uses @nivo/sankey so layout and flow rendering stay robust while we control
 * styling and data shaping in this component.
 *
 * The EV-expansion subtree from the backend (NOPAT, Value Creation,
 * EV from EBIT/Growth, Enterprise Value, Market Cap) is filtered out at the
 * data layer. Those nodes carry valuations that are much larger than operating
 * revenue and visually distort the operating cost flow.
 */
import { useMemo, useId, createElement } from 'react';
import type { ReactNode } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import { useTooltip } from '@nivo/tooltip';
import { Loader2, TrendingUp } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import NoDataState from '../../ui/NoDataState';
import { useSankeyData } from '../hooks/useSankeyData';
import type { RegionConfig } from '../../../types/financial';

interface Props {
  companyName: string;
  year?: number;
  region?: RegionConfig;
  headerRight?: ReactNode;
}

const CHART_HEIGHT = 660;
const SANKEY_MARGIN = { top: 20, right: 220, bottom: 20, left: 160 };

// Names matched against (case-insensitive) `includes` to filter out the EV
// expansion subtree. These nodes are valuation outputs, not cost flows.
const EV_NODE_PATTERNS = [
  'nopat',
  'value creation',
  'ev from',
  'enterprise value',
  'market cap',
];

// Explicit visual ordering makes the flow read naturally left -> right
// and reduces crossovers/kinks in small terminal links.
const FLOW_ORDER_PATTERNS: Array<{ pattern: string; order: number }> = [
  { pattern: 'total revenue', order: 10 },
  { pattern: 'direct cost', order: 20 },
  { pattern: 'gross margin', order: 30 },
  { pattern: 'ebitda', order: 40 },
  { pattern: 'ebit', order: 50 },
  { pattern: 'material costs', order: 60 },
  { pattern: 'stock purchases', order: 70 },
  { pattern: 'inventory change', order: 80 },
  { pattern: 'employee costs', order: 90 },
  { pattern: 'other expenses', order: 100 },
  { pattern: 'depreciation', order: 110 },
  { pattern: 'finance costs', order: 120 },
];

function isEvNode(label: string): boolean {
  const lower = label.toLowerCase();
  return EV_NODE_PATTERNS.some((p) => lower.includes(p));
}

// Strip currency, value and parenthetical noise from backend labels so the
// chart shows just the human-readable name.
function cleanName(label: string): string {
  return label
    .replace(/\u20B9\s?[\d,.]+\s?(Cr|Crore|Lakh|M|B|K)?/gi, '')
    .replace(/\$\s?[\d,.]+\s?(M|B|K|Bn|Mn)?/gi, '')
    .replace(/\([\d.,%\s]+\)/g, '')
    .replace(/ROIC.*WACC.*$/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatValue(v: number, unit: string): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M ${unit}`.trim();
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K ${unit}`.trim();
  return `${v.toFixed(0)} ${unit}`.trim();
}

function formatPct(v: number): string {
  if (!isFinite(v) || v <= 0) return '';
  if (v >= 100) return '100%';
  if (v >= 10) return `${v.toFixed(0)}%`;
  return `${v.toFixed(1)}%`;
}

function formatPctCompact(v: number): string {
  const txt = formatPct(v);
  return txt.endsWith('.0%') ? txt.replace('.0%', '%') : txt;
}

function getFlowOrder(name: string): number {
  const lower = name.toLowerCase();
  const match = FLOW_ORDER_PATTERNS.find(({ pattern }) => lower.includes(pattern));
  return match?.order ?? 999;
}

/**
 * Pastel semantic palette — encodes financial meaning while being gentle on
 * the eyes. Pastels keep the chart legible without visual fatigue.
 *
 *   Sky-blue  → Revenue / income (money arriving)
 *   Green     → Profit / margin nodes (money retained)
 *   Rose/pink → Cost buckets (money spent)
 *   Amber     → Non-cash / financing deductions
 */
function getNodeColor(name: string): string {
  const lower = name.toLowerCase();

  // Root node — soft amber/gold ("revenue = money", warm but not loud)
  if (lower.includes('revenue') || lower.includes('total income') || lower === 'total')
    return '#fef08a'; // yellow-200

  // Retained profit / margin — green pastel spectrum
  if (lower.includes('gross margin') || lower.includes('gross profit'))
    return '#86efac'; // green-300
  if (lower.includes('ebitda'))
    return '#6ee7b7'; // emerald-300
  if (lower.includes('ebit') || lower.includes('operating profit'))
    return '#5eead4'; // teal-300

  // Non-cash / financing items — amber / peach pastels
  if (lower.includes('depreciation') || lower.includes('amortis'))
    return '#fde68a'; // amber-200
  if (lower.includes('finance') || lower.includes('interest'))
    return '#fdba74'; // orange-300

  // Cost categories — rose / pink pastel spectrum
  if (lower.includes('material'))
    return '#fca5a5'; // red-300
  if (lower.includes('stock') || lower.includes('inventory'))
    return '#fed7aa'; // orange-200
  if (
    lower.includes('employee') ||
    lower.includes('personnel') ||
    lower.includes('labour') ||
    lower.includes('labor') ||
    lower.includes('staff')
  )
    return '#f9a8d4'; // pink-300

  // Generic cost / expense bucket — rose-300
  return '#fda4af';
}

interface BackendNode {
  id: string;
  label: string;
  value: number;
}

interface BackendLink {
  source: string;
  target: string;
  value: number;
}

interface BackendPayload {
  company_name?: string;
  fiscal_year?: number;
  year?: number;
  sankey_data?: {
    nodes?: BackendNode[];
    links?: BackendLink[];
  };
  nodes?: BackendNode[];
  links?: BackendLink[];
}

interface NivoNode {
  id: string;
  nodeColor: string;
  rawValue: number;
  flowOrder: number;
}

interface NivoLink {
  source: string;
  target: string;
  value: number;
}

// ── Shared tooltip card style ─────────────────────────────────────────────────

const TOOLTIP_CARD: React.CSSProperties = {
  minWidth: 230,
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 16px 40px rgba(0,0,0,0.10), 0 4px 12px rgba(0,0,0,0.06)',
  fontFamily: 'inherit',
};

const TOOLTIP_LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase' as const,
  color: '#94a3b8',
  marginBottom: 6,
};

// ── Custom smooth links layer ─────────────────────────────────────────────────

/**
 * White-card link tooltip coordinated with the chart's pastel color scheme.
 * Shows a source→target gradient header strip plus color chips beside names.
 */
function LinkTooltipContent({
  link,
  totalRevenue,
  unit,
}: {
  link: {
    source: { id: string; color?: string };
    target: { id: string; color?: string };
    value: number;
  };
  totalRevenue: number;
  unit: string;
}) {
  const pct = totalRevenue > 0 ? (link.value / totalRevenue) * 100 : 0;
  const srcColor = link.source.color || '#93c5fd';
  const tgtColor = link.target.color || '#fda4af';

  return (
    <div style={TOOLTIP_CARD}>
      {/* Gradient header bar encoding source → target */}
      <div
        style={{
          height: 5,
          background: `linear-gradient(90deg, ${srcColor} 0%, ${tgtColor} 100%)`,
        }}
      />
      <div style={{ padding: '10px 14px 12px' }}>
        <div style={TOOLTIP_LABEL}>Flow</div>

        {/* Source → Target row with color chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-block',
              width: 9,
              height: 9,
              borderRadius: 2,
              background: srcColor,
              border: '1px solid rgba(0,0,0,0.08)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
            {link.source.id}
          </span>
          <span style={{ color: '#94a3b8', fontSize: 12, margin: '0 2px' }}>→</span>
          <span
            style={{
              display: 'inline-block',
              width: 9,
              height: 9,
              borderRadius: 2,
              background: tgtColor,
              border: '1px solid rgba(0,0,0,0.08)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
            {link.target.id}
          </span>
        </div>

        {/* Value + % row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid #f1f5f9',
            gap: 16,
          }}
        >
          <div>
            <div style={{ ...TOOLTIP_LABEL, marginBottom: 3 }}>Value</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
              {formatValue(link.value, unit)}
            </div>
          </div>
          {pct > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ ...TOOLTIP_LABEL, marginBottom: 3 }}>of Revenue</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                {formatPct(pct)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a single Sankey link as a smooth cubic bezier ribbon.
 *
 * nivo's default uses curveMonotoneX with control points at only 12 % of the
 * inter-node gap, producing a very tight curve near the node edges.
 * Here the bezier control points sit at the horizontal midpoint (50 %),
 * creating a full smooth S-curve across the entire gap — matching the
 * reference "flowing" look.
 */
function SmoothLink({
  link,
  opacity,
  gradientId,
  setCurrentLink,
  isInteractive,
  totalRevenue,
  unit,
}: {
  link: any;
  opacity: number;
  gradientId: string;
  setCurrentLink: (l: any) => void;
  isInteractive: boolean;
  totalRevenue: number;
  unit: string;
}) {
  const { showTooltipFromEvent, hideTooltip } = useTooltip();

  // Enforce a visual minimum so small-value flows (e.g. Depreciation 2 %)
  // remain visible rather than collapsing to a near-invisible hairline.
  const halfThick = Math.max(3, link.thickness) / 2;
  const sx = link.source.x1;
  const tx = link.target.x0;
  const cpX = (sx + tx) / 2;

  // Top edge: source top → bezier → target top
  // Bottom edge: target bottom → bezier → source bottom
  const d = [
    `M ${sx},${link.pos0 - halfThick}`,
    `C ${cpX},${link.pos0 - halfThick} ${cpX},${link.pos1 - halfThick} ${tx},${link.pos1 - halfThick}`,
    `L ${tx},${link.pos1 + halfThick}`,
    `C ${cpX},${link.pos1 + halfThick} ${cpX},${link.pos0 + halfThick} ${sx},${link.pos0 + halfThick}`,
    'Z',
  ].join(' ');

  return (
    <path
      d={d}
      fill={`url(#${gradientId})`}
      fillOpacity={opacity}
      shapeRendering="geometricPrecision"
      style={{ transition: 'fill-opacity 0.18s ease' }}
      onMouseEnter={
        isInteractive
          ? (e) => {
              setCurrentLink(link);
              showTooltipFromEvent(
                createElement(LinkTooltipContent, { link, totalRevenue, unit }),
                e,
                'left'
              );
            }
          : undefined
      }
      onMouseMove={
        isInteractive
          ? (e) => {
              showTooltipFromEvent(
                createElement(LinkTooltipContent, { link, totalRevenue, unit }),
                e,
                'left'
              );
            }
          : undefined
      }
      onMouseLeave={
        isInteractive
          ? () => {
              setCurrentLink(null);
              hideTooltip();
            }
          : undefined
      }
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ValuationSankeyChart({ companyName, year, region, headerRight }: Props) {
  const { data, isLoading, isError } = useSankeyData(
    companyName ? { company_name: companyName, year, chart_type: 'revenue_to_ev' } : null
  );

  const unit = region?.currency?.unit_abbr ?? '';

  const raw = useMemo<BackendPayload>(() => (data as unknown as BackendPayload) ?? {}, [data]);
  const { backendNodes, backendLinks } = useMemo(
    () => ({
      backendNodes: raw.sankey_data?.nodes ?? raw.nodes ?? [],
      backendLinks: raw.sankey_data?.links ?? raw.links ?? [],
    }),
    [raw]
  );

  const { nivoData, totalRevenue } = useMemo(() => {
    if (backendNodes.length === 0 || backendLinks.length === 0) {
      return { nivoData: { nodes: [] as NivoNode[], links: [] as NivoLink[] }, totalRevenue: 0 };
    }

    // 1) Filter out EV-expansion nodes.
    const keptNodes = backendNodes.filter((n) => !isEvNode(n.label));
    const keptIds = new Set(keptNodes.map((n) => n.id));

    // 2) Map raw id -> human-readable cleaned label, ensuring uniqueness.
    const seen = new Map<string, number>();
    const idToCleanId = new Map<string, string>();
    keptNodes.forEach((n) => {
      const base = cleanName(n.label) || n.id;
      const count = seen.get(base) ?? 0;
      const cleanId = count === 0 ? base : `${base} (${count + 1})`;
      seen.set(base, count + 1);
      idToCleanId.set(n.id, cleanId);
    });

    // 3) Keep links where both endpoints survived, remap ids to cleaned ids.
    const links: NivoLink[] = backendLinks
      .filter((l) => keptIds.has(l.source) && keptIds.has(l.target) && l.value > 0)
      .map((l) => ({
        source: idToCleanId.get(l.source)!,
        target: idToCleanId.get(l.target)!,
        value: l.value,
      }));

    // 4) Drop orphan nodes after filtering (required by nivo).
    const referenced = new Set<string>();
    links.forEach((l) => {
      referenced.add(l.source);
      referenced.add(l.target);
    });

    const nodes: NivoNode[] = keptNodes
      .map((n) => ({
        id: idToCleanId.get(n.id)!,
        nodeColor: getNodeColor(idToCleanId.get(n.id)!),
        rawValue: n.value,
        flowOrder: getFlowOrder(idToCleanId.get(n.id)!),
      }))
      .filter((n) => referenced.has(n.id));

    // 5) Total revenue = root node(s) with no incoming links.
    const incoming = new Set(links.map((l) => l.target));
    const roots = nodes.filter((n) => !incoming.has(n.id));
    const total =
      roots.reduce((sum, n) => sum + n.rawValue, 0) ||
      Math.max(0, ...nodes.map((n) => n.rawValue));

    return { nivoData: { nodes, links }, totalRevenue: total };
  }, [backendNodes, backendLinks]);

  // Stable per-instance ID prefix for SVG gradient IDs (avoids conflicts when
  // multiple charts are mounted simultaneously).
  const rawId = useId();
  const chartId = rawId.replace(/\W/g, '');

  /**
   * Custom "links" layer that replaces nivo's default link rendering.
   *
   * Uses pure cubic bezier curves with midpoint control points (50 % of the
   * inter-node gap) instead of nivo's 12 %, producing a full smooth S-curve.
   * Pastel source→target gradient fills give each flow a natural colour story.
   */
  const smoothLinksLayer = useMemo(() => {
    const rev = totalRevenue;
    const u = unit;
    const prefix = chartId;

    function SmoothLinksLayer({
      links,
      currentNode,
      currentLink,
      isCurrentLink,
      setCurrentLink,
      isInteractive,
    }: {
      links: any[];
      currentNode: any;
      currentLink: any;
      isCurrentLink: (l: any) => boolean;
      setCurrentLink: (l: any) => void;
      isInteractive: boolean;
    }) {
      const hasHover = !!currentNode || !!currentLink;
      return (
        <>
          <defs>
            {links.map((link: any, i: number) => (
              <linearGradient
                key={i}
                id={`${prefix}g${i}`}
                x1="0%"
                x2="100%"
                y1="0%"
                y2="0%"
              >
                <stop offset="0%" stopColor={link.source.color} />
                <stop offset="100%" stopColor={link.target.color} />
              </linearGradient>
            ))}
          </defs>
          <g>
            {links.map((link: any, i: number) => {
              const opacity = hasHover
                ? isCurrentLink(link) ? 0.90 : 0.07
                : 0.78;
              return (
                <SmoothLink
                  key={`${link.source.id}.${link.target.id}.${i}`}
                  link={link}
                  opacity={opacity}
                  gradientId={`${prefix}g${i}`}
                  setCurrentLink={setCurrentLink}
                  isInteractive={isInteractive}
                  totalRevenue={rev}
                  unit={u}
                />
              );
            })}
          </g>
        </>
      );
    }

    return SmoothLinksLayer;
  }, [chartId, totalRevenue, unit]);

  const fiscalYear = raw.fiscal_year ?? raw.year ?? year;
  const displayCompanyName = raw.company_name ?? companyName;
  const subtitle = fiscalYear
    ? `Visual flow of revenue through expenses to enterprise value for ${displayCompanyName} (FY ${fiscalYear})`
    : `Visual flow of revenue through expenses to enterprise value for ${displayCompanyName}`;

  const header = (
    <CardHeader className="!px-6 !py-4 bg-gradient-to-r from-neutral-50/90 to-violet-50/40">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border border-violet-200 bg-white text-violet-700 shadow-sm">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-[22px] leading-none font-semibold text-neutral-800 tracking-tight">
              Revenue to Enterprise Value Flow
            </h3>
            <p className="text-xs text-neutral-500 mt-1.5">
              {fiscalYear ? `FY ${fiscalYear}` : 'Latest fiscal year'}
              {unit ? ` | ${unit}` : ''}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-violet-200/80 bg-white/95 px-2 py-1 shadow-sm">
          {headerRight}
        </div>
      </div>
    </CardHeader>
  );

  const renderState = (content: ReactNode) => (
    <Card className="overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-white via-neutral-50/80 to-violet-50/35 shadow-[0_8px_32px_rgba(15,23,42,0.06)]">
      {header}
      <CardBody className="!px-6 !pt-4 !pb-5">
        <p className="text-sm text-neutral-600">{subtitle}</p>

        {/* Metric badge + colour legend */}
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          {totalRevenue > 0 && (
            <div className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
              Total Revenue: {formatValue(totalRevenue, unit)}
            </div>
          )}
          {[
            { color: '#93c5fd', label: 'Revenue' },
            { color: '#86efac', label: 'Profit / Margin' },
            { color: '#fda4af', label: 'Costs' },
            { color: '#fde68a', label: 'Non-cash / Finance' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span
                style={{ background: color, border: '1px solid rgba(0,0,0,0.08)' }}
                className="inline-block h-2.5 w-2.5 rounded-sm"
              />
              <span className="text-[11px] text-neutral-500">{label}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-neutral-400 italic mt-1.5">
          All values derived from net operating revenue.
        </p>

        {/* Chart area — clean white so pastel flows stand out */}
        <div
          className="relative mt-4 w-full overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
          style={{ height: CHART_HEIGHT }}
        >
          {content}
        </div>
      </CardBody>
    </Card>
  );

  if (isLoading) {
    return renderState(
      <div className="flex h-full items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (isError || !data || nivoData.nodes.length === 0 || nivoData.links.length === 0) {
    return renderState(
      <NoDataState
        asCard={false}
        title="Valuation flow unavailable"
        hint="This chart needs revenue, cost and enterprise-value figures for the selected year — at least one of them is missing."
        minHeight={160}
      />
    );
  }

  return renderState(
    <ResponsiveSankey
      data={nivoData}
      margin={SANKEY_MARGIN}
      align="justify"
      sort={(a: { flowOrder?: number; value: number }, b: { flowOrder?: number; value: number }) => {
        const orderDiff = (a.flowOrder ?? 999) - (b.flowOrder ?? 999);
        if (orderDiff !== 0) return orderDiff;
        return b.value - a.value;
      }}
      colors={(node: { nodeColor?: string }) => node.nodeColor || '#fda4af'}
      nodeOpacity={1}
      nodeHoverOpacity={1}
      nodeHoverOthersOpacity={0.25}
      nodeThickness={10}
      nodeSpacing={20}
      nodeInnerPadding={0}
      nodeBorderWidth={0}
      nodeBorderRadius={3}
      labelPosition="outside"
      labelOrientation="horizontal"
      labelPadding={8}
      label={(node: { id: string; value: number }) => {
        const pct = totalRevenue > 0 ? (node.value / totalRevenue) * 100 : 0;
        if (pct > 0 && pct < 0.65) return '';
        const pctText = formatPctCompact(pct);
        return pctText ? `${node.id} (${pctText})` : node.id;
      }}
      labelTextColor="#1e293b"
      // Custom layer replaces the default 'links' layer with smooth bezier ribbons
      layers={[smoothLinksLayer as any, 'nodes', 'labels', 'legends']}
      theme={{
        labels: {
          text: {
            fontSize: 12,
            fontWeight: 700,
            fill: '#1e293b',
          },
        },
        tooltip: {
          container: {
            background: 'transparent',
            padding: 0,
            boxShadow: 'none',
          },
        },
      }}
      nodeTooltip={({ node }: { node: { id: string; value: number } }) => {
        const pct = totalRevenue > 0 ? (node.value / totalRevenue) * 100 : 0;
        const nodeColor = getNodeColor(node.id);
        return (
          <div style={TOOLTIP_CARD}>
            {/* Solid header strip in the node's pastel color */}
            <div style={{ height: 5, background: nodeColor }} />
            <div style={{ padding: '10px 14px 12px' }}>
              <div style={TOOLTIP_LABEL}>Node</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 9,
                    height: 9,
                    borderRadius: 2,
                    background: nodeColor,
                    border: '1px solid rgba(0,0,0,0.08)',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{node.id}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  paddingTop: 10,
                  borderTop: '1px solid #f1f5f9',
                  gap: 16,
                }}
              >
                <div>
                  <div style={{ ...TOOLTIP_LABEL, marginBottom: 3 }}>Value</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                    {formatValue(node.value, unit)}
                  </div>
                </div>
                {pct > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...TOOLTIP_LABEL, marginBottom: 3 }}>of Revenue</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                      {formatPct(pct)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
      animate={true}
      motionConfig="gentle"
    />
  );
}
